/**
 * MapPage — public map view that composes map layers, search/auth launcher,
 * and read-only detail panels.
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type OSMFeatureSearchResult } from '../lib/api';
import { Map, type MapRef } from '../components/ui/map';
import { useThemeStore, useAuthStore } from '../store';
import { getMapStyles } from '../lib/map-styles';
import type {
  Segment,
  MapPost,
} from '../types/map';

import { WaterwayLineLayer } from '../components/map/WaterwayLineLayer';
import { ViewportWaterwayLoader } from '../components/map/ViewportWaterwayLoader';
import { SegmentPointLayer } from '../components/map/SegmentPointLayer';
import { MapCameraControls } from '../components/map/MapCameraControls';
import { Basemap3DBuildingsLayer } from '../components/map/Basemap3DBuildingsLayer';

import { SegmentPanel } from '../components/map/SegmentPanel';

import { SearchIcon, UserIcon } from '../components/ui/icons';

interface MapPageProps {
  showSearchBar?: boolean;
  showLandingAuthButton?: boolean;
}

export function getPublicMapSearchResultLabel(
  result: Pick<OSMFeatureSearchResult, 'featureType' | 'subtype'>,
): string {
  const typeLabel = result.featureType === 'bridge'
    ? 'OSM Bridge'
    : result.featureType === 'lock'
      ? 'OSM Lock'
      : result.featureType === 'weir'
        ? 'OSM Weir'
      : 'OSM Waterway';
  if (!result.subtype) return typeLabel;
  return `${typeLabel} • ${result.subtype.replace(/_/g, ' ')}`;
}

export const PUBLIC_MAP_SEARCH_INPUT_CLASSNAME =
  'w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-lg shadow-lg border-0 focus:ring-2 focus:ring-primary-500';

export default function MapPage({
  showSearchBar = true,
  showLandingAuthButton = false,
}: MapPageProps) {
  const mapRef = useRef<MapRef | null>(null);
  const searchRef = useRef<HTMLDivElement | null>(null);
  const searchRequestIdRef = useRef(0);
  const theme = useThemeStore((state) => state.theme);
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();

  const [waterwayGeoJson, setWaterwayGeoJson] = useState<GeoJSON.FeatureCollection>({
    type: 'FeatureCollection',
    features: [],
  });
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(true);
  const [waterwaysLoading, setWaterwaysLoading] = useState(false);
  const [maptilerKey, setMaptilerKey] = useState('');
  const [mapConfigLoading, setMapConfigLoading] = useState(true);
  const [mapConfigError, setMapConfigError] = useState<string | null>(null);

  const [selectedSegment, setSelectedSegment] = useState<Segment | null>(null);
  const [segmentPosts, setSegmentPosts] = useState<MapPost[]>([]);
  const [showAuthMenu, setShowAuthMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<OSMFeatureSearchResult[]>([]);

  const [prefersDark, setPrefersDark] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : false,
  );

  const mapTheme = useMemo<'light' | 'dark'>(
    () =>
      theme === 'dark' || (theme === 'auto' && prefersDark) ? 'dark' : 'light',
    [theme, prefersDark],
  );

  const emptyHighlightGeoJson = useMemo<GeoJSON.FeatureCollection>(
    () => ({ type: 'FeatureCollection', features: [] }),
    [],
  );
  const noSelectedWaterwayIds = useMemo<string[]>(() => [], []);

  const segmentsGeoJson = useMemo<GeoJSON.FeatureCollection>(() => {
    const features: GeoJSON.Feature[] = segments
      .filter((s) => Number.isFinite(s.centroidLat) && Number.isFinite(s.centroidLng))
      .map((s) => ({
        type: 'Feature',
        properties: {
          id: s.id,
          displayName: s.displayName,
          waterwayName: s.waterwayName,
          postCount: Number(s.postCount) || 0,
        },
        geometry: { type: 'Point', coordinates: [s.centroidLng, s.centroidLat] },
      }));
    return { type: 'FeatureCollection', features } as GeoJSON.FeatureCollection;
  }, [segments]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent | MediaQueryList) =>
      setPrefersDark(event.matches);

    if (typeof mq.addEventListener === 'function') {
      const listener = (event: MediaQueryListEvent) => onChange(event);
      mq.addEventListener('change', listener);
      return () => mq.removeEventListener('change', listener);
    }

    if (typeof mq.addListener === 'function') {
      const listener = (event: MediaQueryListEvent) => onChange(event);
      mq.addListener(listener);
      return () => mq.removeListener(listener);
    }

    return undefined;
  }, []);

  const loadMapConfig = useCallback(async () => {
    setMapConfigLoading(true);
    setMapConfigError(null);
    try {
      const cfg: { maptilerApiKey: string } = await api.config.getPublic();
      if (!cfg?.maptilerApiKey) {
        setMaptilerKey('');
        setMapConfigError('Map configuration is unavailable right now.');
        return;
      }
      setMaptilerKey(cfg.maptilerApiKey);
    } catch (err) {
      console.error('Failed to load map config:', err);
      setMaptilerKey('');
      setMapConfigError('Could not load map configuration. Please retry.');
    } finally {
      setMapConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMapConfig();
  }, [loadMapConfig]);

  useEffect(() => {
    if (!showSearchBar) return;
    if (!searchOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (searchRef.current?.contains(target)) return;
      setSearchOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [searchOpen, showSearchBar]);

  useEffect(() => {
    if (!showSearchBar) return;
    const query = searchQuery.trim();
    if (query.length < 2) {
      searchRequestIdRef.current += 1;
      setSearchLoading(false);
      setSearchError(null);
      setSearchResults([]);
      return;
    }

    const requestId = searchRequestIdRef.current + 1;
    searchRequestIdRef.current = requestId;
    const timer = setTimeout(() => {
      setSearchLoading(true);
      setSearchError(null);
      void api.waterways.searchOsmFeatures({ query, limit: 12 })
        .then((results) => {
          if (searchRequestIdRef.current !== requestId) return;
          setSearchResults(results);
          setSearchOpen(true);
        })
        .catch((error) => {
          if (searchRequestIdRef.current !== requestId) return;
          console.error('Failed to search map OSM features:', error);
          setSearchResults([]);
          setSearchError('Search failed');
          setSearchOpen(true);
        })
        .finally(() => {
          if (searchRequestIdRef.current !== requestId) return;
          setSearchLoading(false);
        });
    }, 180);

    return () => clearTimeout(timer);
  }, [searchQuery, showSearchBar]);

  const handleWaterwayDataLoaded = useCallback(
    (data: GeoJSON.FeatureCollection) => {
      setWaterwayGeoJson({
        type: 'FeatureCollection',
        features: data.features,
      });
    },
    [],
  );

  useEffect(() => {
    const loadData = async () => {
      try {
        const segmentsData = await api.waterways.getSegments(undefined, { includeCanalplan: false });
        setSegments(segmentsData);
      } catch (err) {
        console.error('Failed to load waterways.segments:', err);
      }
      setLoading(false);
    };
    loadData();
  }, []);

  const handleSegmentClick = useCallback(async (segment: Segment) => {
    setSelectedSegment(segment);
    setSegmentPosts([]);
    try {
      const posts = await api.waterways.getSegmentPosts(segment.id);
      setSegmentPosts(posts);
    } catch (err) {
      console.error('Failed to load segment posts:', err);
    }
  }, []);

  const handleSelectSearchResult = useCallback((result: OSMFeatureSearchResult) => {
    setSearchQuery(result.name);
    setSearchResults([]);
    setSearchError(null);
    setSearchOpen(false);

    const map = mapRef.current;
    if (!map) return;
    const nextZoom = Math.max(map.getZoom(), 13);
    map.flyTo({
      center: [result.longitude, result.latitude],
      zoom: nextZoom,
      duration: 900,
      essential: true,
    });
  }, []);

  return (
    <div className="h-screen relative">
      <div className="absolute inset-0 z-0" data-map-theme={mapTheme}>
        {maptilerKey && (
          <Map
            ref={mapRef}
            center={[-1.5, 52.5]}
            zoom={7}
            styles={getMapStyles(maptilerKey)}
            theme={mapTheme}
            dragRotate
            pitchWithRotate
          >
            <MapCameraControls />
            <Basemap3DBuildingsLayer enabled={mapTheme === 'dark'} />
            <ViewportWaterwayLoader
              onDataLoaded={handleWaterwayDataLoaded}
              onLoadingChange={setWaterwaysLoading}
            />

            {waterwayGeoJson.features.length > 0 && (
              <WaterwayLineLayer
                waterwayGeoJson={waterwayGeoJson}
                highlightGeoJson={emptyHighlightGeoJson}
                selectedWaterwayIds={noSelectedWaterwayIds}
                isAdmin={false}
                onSelectedWaterwayIdsChange={() => undefined}
              />
            )}

            {segmentsGeoJson.features.length > 0 && (
              <SegmentPointLayer
                segmentsGeoJson={segmentsGeoJson}
                segments={segments}
                onSegmentClick={handleSegmentClick}
              />
            )}

          </Map>
        )}
        {!maptilerKey && !mapConfigLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/90 dark:bg-slate-900/90 z-10 px-4">
            <div className="max-w-sm w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 text-center space-y-3">
              <h2 className="text-base font-semibold text-slate-900 dark:text-white">
                Map unavailable
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {mapConfigError || 'Map configuration failed to load.'}
              </p>
              <button
                type="button"
                onClick={loadMapConfig}
                className="btn btn-primary w-full"
              >
                Retry map load
              </button>
            </div>
          </div>
        )}
      </div>

      {(loading || mapConfigLoading) && (
        <div className="absolute inset-0 bg-white/80 dark:bg-slate-900/80 flex items-center justify-center z-10">
          <div className="text-center">
            <div className="animate-spin w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-slate-600 dark:text-slate-400">Loading map...</p>
          </div>
        </div>
      )}

      {waterwaysLoading && !loading && !mapConfigLoading && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-20">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 px-4 py-2 flex items-center gap-2">
            <div className="animate-spin w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full" />
            <span className="text-sm text-slate-700 dark:text-slate-300">Loading waterways...</span>
          </div>
        </div>
      )}

      {showSearchBar && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 w-full max-w-md px-4">
          <div className="relative" ref={searchRef}>
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(event) => {
                setSearchQuery(event.target.value);
                if (!searchOpen) setSearchOpen(true);
              }}
              onFocus={() => {
                if (searchLoading || searchError || searchResults.length > 0) {
                  setSearchOpen(true);
                }
              }}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  setSearchOpen(false);
                  return;
                }
                if (event.key === 'Enter' && searchResults.length > 0) {
                  event.preventDefault();
                  handleSelectSearchResult(searchResults[0]);
                }
              }}
              placeholder="Search OSM waterways, bridges, locks..."
              className={PUBLIC_MAP_SEARCH_INPUT_CLASSNAME}
            />

            {searchOpen && (
              <div className="absolute left-0 right-0 mt-1.5 max-h-72 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl">
                {searchLoading ? (
                  <div className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">Searching...</div>
                ) : searchError ? (
                  <div className="px-3 py-2 text-xs text-red-500 dark:text-red-400">{searchError}</div>
                ) : searchQuery.trim().length < 2 ? (
                  <div className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">Type at least 2 characters.</div>
                ) : searchResults.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">No matches found.</div>
                ) : (
                  <ul className="py-1">
                    {searchResults.map((result) => (
                      <li key={`${result.dataset}:${result.id}`}>
                        <button
                          type="button"
                          onClick={() => handleSelectSearchResult(result)}
                          className="w-full px-3 py-2 text-left hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                        >
                          <div className="text-sm text-slate-800 dark:text-slate-100 truncate">
                            {result.name}
                          </div>
                          <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                            {getPublicMapSearchResultLabel(result)}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {showLandingAuthButton && !user && (
        <div className="absolute left-4 bottom-6 z-30">
          {showAuthMenu && (
            <div className="mb-3 w-56 rounded-xl bg-white dark:bg-slate-800 shadow-xl border border-slate-200 dark:border-slate-700 p-3 space-y-2">
              <button
                onClick={() => navigate('/login')}
                className="btn btn-primary w-full text-sm py-2"
              >
                Login
              </button>
              <button
                disabled
                className="btn w-full text-sm py-2 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 cursor-not-allowed"
                title="Registration coming soon"
              >
                Register (Coming Soon)
              </button>
            </div>
          )}
          <button
            onClick={() => setShowAuthMenu((prev) => !prev)}
            className="w-12 h-12 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-lg text-slate-700 dark:text-slate-200 flex items-center justify-center"
            aria-label="Open login options"
            title="Account"
          >
            <UserIcon className="w-6 h-6" />
          </button>
        </div>
      )}

      {selectedSegment && (
        <SegmentPanel
          segment={selectedSegment}
          posts={segmentPosts}
          onClose={() => setSelectedSegment(null)}
        />
      )}
    </div>
  );
}
